import { useCallback, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

import { ZodProvider } from "@autoform/zod";
import { useWarnAboutChange } from "@refinedev/core";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { LoadingButton } from "@/components/common/loading-button";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { isFormDirty } from "@/lib/utils";
import {
  type EventGroupFormType,
  eventGroupFormSchema,
} from "@/pages/event-groups/schemas/event-group.form";

const schemaProvider = new ZodProvider(eventGroupFormSchema);

/**
 * The name, text and photos of a group, for the create and the edit page. `onSave` answers whether
 * the group was saved, so the unsaved-changes warning can come back when it was not.
 */
export function EventGroupForm({
  defaultValues,
  submitLabel,
  onSave,
  lockedReason,
}: {
  defaultValues: Partial<EventGroupFormType>;
  submitLabel: string;
  onSave: (values: EventGroupFormType) => Promise<boolean>;
  /** Why the group cannot be saved right now. While it is set, Save is off and the reason shows beside it. */
  lockedReason?: string;
}) {
  const { setWarnWhen } = useWarnAboutChange();
  const [saving, setSaving] = useState(false);

  const watchForChanges = useCallback(
    (form: UseFormReturn<EventGroupFormType, any, EventGroupFormType>) => {
      form.watch((value) => {
        setWarnWhen(
          form.formState.isDirty &&
            isFormDirty(
              form.formState.dirtyFields as Record<string, unknown>,
              value as Record<string, unknown>,
              form.formState.defaultValues as Record<string, unknown>,
            ),
        );
      });
    },
    [setWarnWhen],
  );

  async function submit(values: EventGroupFormType) {
    setSaving(true);
    // Off before the save, so the page a create opens next is not held back by the warning.
    setWarnWhen(false);
    try {
      if (!(await onSave(values))) setWarnWhen(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AutoForm
      schema={schemaProvider}
      defaultValues={defaultValues}
      onSubmit={submit}
      onFormInit={watchForChanges}
      {...injectCustomAutoFormComponents()}
    >
      <BottomActionBar>
        <LoadingButton isLoading={saving} disabled={saving || !!lockedReason}>
          {submitLabel}
        </LoadingButton>
        {lockedReason && (
          <p className="self-center pl-3 text-sm text-muted-foreground">
            {lockedReason}
          </p>
        )}
      </BottomActionBar>
    </AutoForm>
  );
}
